import { useMemo } from "react";
import { differenceInDays } from "date-fns";
import { calcularStatus } from "../types";
import type { ContaPagarResumoDto, ContaReceberResumoDto } from "../types";

interface FinanceKPIsProps {
  contasPagar: ContaPagarResumoDto[];
  contasReceber: ContaReceberResumoDto[];
}

export const useFinanceKPIs = ({ contasPagar, contasReceber }: FinanceKPIsProps) => {
  // Taxa de Inadimplência (contas atrasadas / total de contas)
  const taxaInadimplencia = useMemo(() => {
    const totalContas = contasReceber.length;
    if (totalContas === 0) return 0;

    const contasAtrasadas = contasReceber.filter(
      c => calcularStatus(c.isPago, c.dtVencimento) === "atrasado"
    ).length;
    return (contasAtrasadas / totalContas) * 100;
  }, [contasReceber]);

  // Prazo Médio de Recebimento (em dias)
  const prazoMedioRecebimento = useMemo(() => {
    const contasRecebidas = contasReceber.filter(
      c => c.isPago && c.dtPagamento
    );

    if (contasRecebidas.length === 0) return 0;

    const totalDias = contasRecebidas.reduce((sum, conta) => {
      const diasAtraso = differenceInDays(
        new Date(conta.dtPagamento!),
        new Date(conta.dtVencimento)
      );
      return sum + diasAtraso;
    }, 0);

    return Math.round(totalDias / contasRecebidas.length);
  }, [contasReceber]);

  // Prazo Médio de Pagamento (em dias)
  const prazoMedioPagamento = useMemo(() => {
    const contasPagas = contasPagar.filter(
      c => c.isPago && c.dtPagamento
    );

    if (contasPagas.length === 0) return 0;

    const totalDias = contasPagas.reduce((sum, conta) => {
      const diasAtraso = differenceInDays(
        new Date(conta.dtPagamento!),
        new Date(conta.dtVencimento)
      );
      return sum + diasAtraso;
    }, 0);

    return Math.round(totalDias / contasPagas.length);
  }, [contasPagar]);

  // Ticket Médio de Recebimento
  const ticketMedioRecebimento = useMemo(() => {
    if (contasReceber.length === 0) return 0;
    const total = contasReceber.reduce((sum, c) => sum + c.valorReceber, 0);
    return total / contasReceber.length;
  }, [contasReceber]);

  // Ticket Médio de Pagamento
  const ticketMedioPagamento = useMemo(() => {
    if (contasPagar.length === 0) return 0;
    const total = contasPagar.reduce((sum, c) => sum + c.valorTotal, 0);
    return total / contasPagar.length;
  }, [contasPagar]);

  // Taxa de Pontualidade no Recebimento
  const taxaPontualidadeRecebimento = useMemo(() => {
    const contasRecebidas = contasReceber.filter(c => c.isPago && c.dtPagamento);
    if (contasRecebidas.length === 0) return 100;

    const recebidosNoPrazo = contasRecebidas.filter(c => {
      return new Date(c.dtPagamento!) <= new Date(c.dtVencimento);
    }).length;

    return (recebidosNoPrazo / contasRecebidas.length) * 100;
  }, [contasReceber]);

  // Taxa de Pontualidade no Pagamento
  const taxaPontualidadePagamento = useMemo(() => {
    const contasPagas = contasPagar.filter(c => c.isPago && c.dtPagamento);
    if (contasPagas.length === 0) return 100;

    const pagosNoPrazo = contasPagas.filter(c => {
      return new Date(c.dtPagamento!) <= new Date(c.dtVencimento);
    }).length;

    return (pagosNoPrazo / contasPagas.length) * 100;
  }, [contasPagar]);

  return {
    taxaInadimplencia,
    prazoMedioRecebimento,
    prazoMedioPagamento,
    ticketMedioRecebimento,
    ticketMedioPagamento,
    taxaPontualidadeRecebimento,
    taxaPontualidadePagamento,
  };
};
